import { useNavigate } from "react-router-dom";
import type { Notification } from "../../hooks/useNotifications";
import { useAnalytics } from "../../lib/analytics";
import styles from "./NotificationItem.module.css";

interface NotificationItemProps {
	notification: Notification;
	onMarkAsRead: (id: string) => void | Promise<void>;
	onActionClick?: (
		notification: Notification,
		action: "accept" | "decline",
	) => void;
}

export function NotificationItem({
	notification,
	onMarkAsRead,
	onActionClick,
}: NotificationItemProps) {
	const navigate = useNavigate();
	const analytics = useAnalytics();

	const formatTimeAgo = (date: Date | string) => {
		const now = new Date();
		const notificationDate = new Date(date);
		const diffInMs = now.getTime() - notificationDate.getTime();
		const diffInMinutes = Math.floor(diffInMs / 60000);
		const diffInHours = Math.floor(diffInMinutes / 60);
		const diffInDays = Math.floor(diffInHours / 24);

		if (diffInMinutes < 60) {
			return `${diffInMinutes}m`;
		}
		if (diffInHours < 24) {
			return `${diffInHours}h`;
		}
		if (diffInDays < 7) {
			return `${diffInDays}d`;
		}
		return notificationDate.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
		});
	};

	const handleClick = async () => {
		analytics.logEvent("notification_clicked", notification.type);

		// Mark as read first
		if (!notification.isRead) {
			analytics.logEvent("notification_marked_as_read");
			await onMarkAsRead(notification.id);
		}

		// Handle navigation
		if (notification.actionUrl) {
			analytics.logEvent("notification_action_taken", notification.type);
			setTimeout(() => {
				navigate(notification.actionUrl as string);
			}, 100);
		}
	};

	const getAvatarContent = () => {
		const metadata = notification.metadata as {
			avatarUrl?: string;
			senderName?: string;
		};

		if (metadata?.avatarUrl) {
			return (
				<img
					src={metadata.avatarUrl}
					alt={metadata.senderName || "Sender"}
					className={styles.avatarImage}
				/>
			);
		}

		// Default avatar with initials
		const initials = metadata?.senderName
			? metadata.senderName
					.split(" ")
					.map((n) => n[0])
					.join("")
					.toUpperCase()
					.slice(0, 2)
			: "N";

		return (
			<div className={styles.avatarDefault}>
				<span className={styles.avatarInitials}>{initials}</span>
			</div>
		);
	};

	const metadata = notification.metadata as { showActions?: boolean };
	const showActionButtons =
		notification.type === "invitation" && metadata?.showActions;

	return (
		<button
			type="button"
			className={`${styles.item} ${!notification.isRead ? styles.unread : ""}`}
			onClick={handleClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					handleClick();
				}
			}}
			aria-label={`Notification: ${notification.title}`}
		>
			{/* Unread indicator */}
			{!notification.isRead && <div className={styles.unreadIndicator} />}

			{/* Avatar */}
			<div className={styles.avatar}>{getAvatarContent()}</div>

			{/* Content */}
			<div className={styles.content}>
				{/* Header with title and time */}
				<div className={styles.headerRow}>
					<h4
						className={`${styles.title} ${!notification.isRead ? styles.unread : ""}`}
					>
						{notification.title}
					</h4>
					<span className={styles.time}>
						{formatTimeAgo(notification.createdAt)}
					</span>
				</div>

				{/* Content */}
				<p className={styles.message}>{notification.content}</p>

				{/* Action buttons (only for invitations with actions) */}
				{showActionButtons && onActionClick && (
					<div className={styles.actions}>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								analytics.logEvent(
									"notification_action_accept",
									notification.type,
								);
								onActionClick(notification, "accept");
							}}
							className={styles.acceptButton}
						>
							Accept
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								analytics.logEvent(
									"notification_action_decline",
									notification.type,
								);
								onActionClick(notification, "decline");
							}}
							className={styles.declineButton}
						>
							Decline
						</button>
					</div>
				)}

				{/* Priority indicator */}
				{(notification.metadata as { priority?: string })?.priority ===
					"high" ||
				(notification.metadata as { priority?: string })?.priority ===
					"urgent" ? (
					<div className={styles.priority}>
						<div
							className={`${styles.priorityDot} ${(notification.metadata as { priority?: string })?.priority === "urgent" ? styles.urgent : styles.high}`}
						/>
						<span
							className={`${styles.priorityText} ${(notification.metadata as { priority?: string })?.priority === "urgent" ? styles.urgent : styles.high}`}
						>
							{(notification.metadata as { priority?: string })?.priority ===
							"urgent"
								? "Urgent"
								: "High Priority"}
						</span>
					</div>
				) : null}
			</div>
		</button>
	);
}
